import { useCallback, useEffect, useRef } from "react";

import { INTERACTIVE_ELEMENTS_SELECTOR } from "./constants";

export interface UseInteractiveNavigationArgs {
    isActive: boolean;
}

/**
 * Get all interactive elements within a container
 */
const getInteractiveElements = (container: HTMLElement | null): HTMLElement[] => {
    if (!container) {
        return [];
    }
    return Array.from(container.querySelectorAll<HTMLElement>(INTERACTIVE_ELEMENTS_SELECTOR));
};

/**
 * Calculate 2D positions of elements for arrow key navigation
 */
const getElementPositions = (elements: HTMLElement[]) => {
    return elements.map(el => {
        const rect = el.getBoundingClientRect();
        return {
            element: el,
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2,
            rect,
        };
    });
};

/**
 * Find the best element to focus based on direction and current position
 */
const findElementInDirection = (
    positions: ReturnType<typeof getElementPositions>,
    currentIndex: number,
    direction: "left" | "right" | "up" | "down",
): number => {
    if (currentIndex < 0 || currentIndex >= positions.length) {
        return -1;
    }

    const current = positions[currentIndex];
    const candidates = positions.filter((_, idx) => idx !== currentIndex);

    let bestIndex = -1;
    let bestScore = Infinity;

    for (let i = 0; i < candidates.length; i++) {
        const candidate = candidates[i];
        const originalIndex = positions.indexOf(candidate);

        // Calculate directional vector
        const dx = candidate.x - current.x;
        const dy = candidate.y - current.y;

        // Filter by direction
        let isInDirection = false;
        let primaryDistance = 0;
        let secondaryDistance = 0;

        switch (direction) {
            case "right":
                isInDirection = dx > 5; // 5px threshold
                primaryDistance = dx;
                secondaryDistance = Math.abs(dy);
                break;
            case "left":
                isInDirection = dx < -5;
                primaryDistance = Math.abs(dx);
                secondaryDistance = Math.abs(dy);
                break;
            case "down":
                isInDirection = dy > 5;
                primaryDistance = dy;
                secondaryDistance = Math.abs(dx);
                break;
            case "up":
                isInDirection = dy < -5;
                primaryDistance = Math.abs(dy);
                secondaryDistance = Math.abs(dx);
                break;
        }

        if (!isInDirection) {
            continue;
        }

        // Score: prioritize primary direction, then minimize secondary offset
        const score = primaryDistance + secondaryDistance * 2;

        if (score < bestScore) {
            bestScore = score;
            bestIndex = originalIndex;
        }
    }

    return bestIndex;
};

const directionMap = {
    ArrowLeft: "left",
    ArrowRight: "right",
    ArrowUp: "up",
    ArrowDown: "down",
} as const;

function isDirectionKey(key: string): key is keyof typeof directionMap {
    return key in directionMap;
}

/**
 * Hook to manage navigation within interactive elements of a list item
 * Supports both arrow keys and Tab navigation
 */
export const useInteractiveNavigation = ({ isActive }: UseInteractiveNavigationArgs) => {
    const containerRef = useRef<HTMLDivElement | null>(null);

    // Focus first interactive element when entering interaction mode
    useEffect(() => {
        if (!isActive || !containerRef.current) {
            return;
        }

        const elements = getInteractiveElements(containerRef.current);
        if (elements.length > 0) {
            elements[0].focus();
        }
    }, [isActive]);

    // Handle keyboard navigation (Arrow keys and Tab)
    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (!isActive || !containerRef.current) {
                return;
            }

            const elements = getInteractiveElements(containerRef.current);
            if (elements.length === 0) {
                return;
            }

            const currentElement = document.activeElement;

            if (!(currentElement instanceof HTMLElement)) {
                return;
            }

            const currentIndex = elements.indexOf(currentElement);

            // Handle Tab navigation (with wrapping)
            if (e.key === "Tab") {
                e.preventDefault();
                const nextIndex = e.shiftKey
                    ? currentIndex <= 0
                        ? elements.length - 1
                        : currentIndex - 1
                    : currentIndex >= elements.length - 1
                      ? 0
                      : currentIndex + 1;

                elements[nextIndex].focus();
                return;
            }

            // Handle Arrow key navigation
            if (!isDirectionKey(e.key) || currentIndex === -1) {
                return;
            }

            e.preventDefault();
            const positions = getElementPositions(elements);
            const nextIndex = findElementInDirection(positions, currentIndex, directionMap[e.key]);

            if (nextIndex >= 0) {
                elements[nextIndex].focus();
            }
        },
        [isActive],
    );

    useEffect(() => {
        if (!isActive) {
            return;
        }
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [isActive, handleKeyDown]);

    return containerRef;
};
